import _Paper from "@mui/material/Paper";
import _TableContainer from "@mui/material/TableContainer";
import _Table from "@mui/material/Table";
import _TableBody from "@mui/material/TableBody";
import _TableHead from "@mui/material/TableHead";
import _TableRow from "@mui/material/TableRow";
import _TableCell from "@mui/material/TableCell";
import _Checkbox from "@mui/material/Checkbox";
import _Skeleton from "@mui/material/Skeleton";
import _CircularProgress from "@mui/material/CircularProgress";
import _Tabs from "@mui/material/Tabs";
import _Tab from "@mui/material/Tab";
import _Box from "@mui/material/Box";
import _Typography from "@mui/material/Typography";
import { useTheme } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import individualBronzeMedal from '../../assets/images/medals/bronze-2.svg';
import teamBronzeMedal from '../../assets/images/medals/bronze.svg';
import individualGoldMedal from '../../assets/images/medals/gold-2.svg';
import teamGoldMedal from '../../assets/images/medals/gold.svg';
import individualSilverMedal from '../../assets/images/medals/silver-2.svg';
import teamSilverMedal from '../../assets/images/medals/silver.svg';
import stars from '../../assets/images/medals/stars.svg';
import { defaultEnvironment } from '../../environment';
import { useCancellablePromise, useChallenge, useUnits } from '../../hooks';
import { GeogroupService } from '../../services';
import { Button as CustomButton } from '../button';
import { ContactDialog } from '../contact-dialog';
import { ContactIcon, ExportIcon } from '../icons';
import { UserAvatar } from '../user';
import FakeItems, { fakeEmployees } from './fake-items';
import Item from './item';
const companyTabs = [{
  key: 'individual',
  labelKey: 'commons.challenge_details.competition_types.individual'
}, {
  key: 'companyTeam',
  labelKey: 'commons.challenge_details.competition_types.teams'
}, {
  key: 'companySite',
  labelKey: 'commons.challenge_details.competition_types.sites'
}, {
  key: 'companySubsidiary',
  labelKey: 'commons.challenge_details.competition_types.subsidiaries'
}, {
  key: 'companyRegion',
  labelKey: 'commons.challenge_details.competition_types.regions'
}];
const podiumRankMap = {
  1: {
    bgColor: '#ECFBF4',
    heightRatio: 1,
    individualMedal: individualGoldMedal,
    teamMedal: teamGoldMedal
  },
  2: {
    bgColor: '#F3F6FF',
    heightRatio: 0.95,
    individualMedal: individualSilverMedal,
    teamMedal: teamSilverMedal
  },
  3: {
    bgColor: '#FFEEE0',
    heightRatio: 0.9,
    individualMedal: individualBronzeMedal,
    teamMedal: teamBronzeMedal
  }
};
const individualChallengeLeaderboardPageSize = 100;
export function ChallengeLeaderboard({
  Button: _Button,
  Dialog,
  FakeItemsCard,
  disableLeaderboardRetrieval,
  fullLeaderboardsDisabled: _fullLeaderboardsDisabled,
  disableLeaderboardRestriction,
  disableTitle,
  enablePodium,
  disableContact,
  currentPartner,
  geogroup,
  currentUser,
  teams,
  subsidiaries,
  regions,
  sites,
  userType,
  downloading,
  exporting,
  enableCompetitionTypes,
  challenge,
  isEventWithOffer,
  userChallenge,
  displayType,
  leaderboard,
  count,
  subgroup,
  setDisplayType,
  setLeaderboard,
  setCount,
  onRowClick,
  handleExport,
  rankField,
  rankFieldLabel
}) {
  const [initialized, setInitialized] = useState(false);
  const [podium, setPodium] = useState();
  const [closeLeaderboard, setCloseLeaderboard] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [selectedUsers, selectUsers] = useState({});
  const [contactDialogOpen, openContactDialog] = useState(false);
  const initialDisplayType = useRef(null);
  const podiumInitialized = useRef(false);
  const {
    t
  } = useTranslation();
  const {
    palette
  } = useTheme();
  const {
    enqueueSnackbar
  } = useSnackbar();
  const {
    cancellablePromise,
    cancelPromises
  } = useCancellablePromise();
  const {
    cancellablePromise: cancellableCloseLeaderboardPromise,
    cancelPromises: cancelCloseLeaderboardPromise
  } = useCancellablePromise();
  const {
    getProgressValue
  } = useChallenge();
  const {
    toOrdinal
  } = useUnits();
  const fullLeaderboardsDisabled = _fullLeaderboardsDisabled && !disableLeaderboardRestriction;
  useEffect(() => {
    setInitialized(true);
  }, []);
  useEffect(() => {
    if (initialized && challenge && displayType && !disableLeaderboardRetrieval) {
      if (!initialDisplayType.current) initialDisplayType.current = displayType;
      getLeaderboard(1);
    }
    return () => {
      cancelPromises();
      setPage(1);
      if (!disableLeaderboardRetrieval) setLeaderboard?.(undefined);
    };
  }, [initialized, challenge, displayType]);
  useEffect(() => {
    if (fullLeaderboardsDisabled && currentUser && challenge && displayType === 'individual' && userChallenge && userChallenge.leaderboardIndex !== undefined && userChallenge.leaderboardIndex >= defaultEnvironment.freeOfferEmployeesLeaderboardsMaxRowsCount) {
      getCloseLeaderboard(currentUser, challenge, userChallenge.leaderboardIndex + 1);
    }
    return () => {
      cancelCloseLeaderboardPromise();
      setCloseLeaderboard([]);
    };
  }, [fullLeaderboardsDisabled, challenge, userChallenge, displayType]);
  useEffect(() => {
    if (initialized && page > 1) {
      getLeaderboard(page);
    }
  }, [page]);
  async function getLeaderboard(page) {
    if (!challenge || !displayType || disableLeaderboardRetrieval) return;
    setLoading(true);
    try {
      if (displayType === 'individual') {
        const {
          leaderboard: pageLeaderboard,
          count
        } = await cancellablePromise(GeogroupService.getChallengeLeaderboard(challenge.id, {
          page,
          pageSize: fullLeaderboardsDisabled ? defaultEnvironment.freeOfferEmployeesLeaderboardsMaxRowsCount : individualChallengeLeaderboardPageSize,
          subgroup
        }));
        if (enablePodium && !podiumInitialized.current && initialDisplayType.current === displayType) {
          setPodium(pageLeaderboard.length < 3 ? null : [pageLeaderboard[1], pageLeaderboard[0], pageLeaderboard[2]]);
          podiumInitialized.current = true;
        }
        const displayedLeaderboard = page === 1 ? pageLeaderboard : [...(leaderboard || []), ...pageLeaderboard];
        setCount?.(Math.min(count, challenge.nbMembers));
        setLeaderboard?.(displayedLeaderboard);
      } else if (currentPartner) {
        if (fullLeaderboardsDisabled && defaultEnvironment.freeOfferSubgroupsLeaderboardsMaxRowsCount <= 0) {
          setPodium(null);
          setCount?.(10);
          setLeaderboard?.([]);
          podiumInitialized.current = true;
        } else {
          const leaderboard = await cancellablePromise(GeogroupService.getChallengeLeaderboardBySubGroups(currentPartner, challenge.id, {
            subGroupDisplay: displayType
          }));
          if (enablePodium && !podiumInitialized.current && initialDisplayType.current === displayType) {
            setPodium(leaderboard.length < 3 ? null : [leaderboard[1], leaderboard[0], leaderboard[2]]);
            podiumInitialized.current = true;
          }
          setCount?.(leaderboard.length);
          setLeaderboard?.(fullLeaderboardsDisabled ? [...leaderboard].slice(0, defaultEnvironment.freeOfferSubgroupsLeaderboardsMaxRowsCount) : leaderboard);
        }
      }
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        enqueueSnackbar(t('commons.challenge_details.server_error_awards'), {
          variant: 'error'
        });
      }
    }
    setLoading(false);
  }
  async function getCloseLeaderboard(currentUser, challenge, leaderboardIndex) {
    try {
      const closeLeaderboard = (await cancellableCloseLeaderboardPromise(Promise.all([leaderboardIndex % 3 === 1 && leaderboardIndex > defaultEnvironment.freeOfferEmployeesLeaderboardsMaxRowsCount + 1 ? GeogroupService.getChallengeLeaderboard(challenge.id, {
        page: leaderboardIndex - 1,
        pageSize: 1,
        subgroup
      }) : {
        leaderboard: []
      }, GeogroupService.getChallengeLeaderboard(challenge.id, {
        page: Math.ceil(leaderboardIndex / 3),
        pageSize: 3,
        subgroup
      }), leaderboardIndex % 3 === 0 ? GeogroupService.getChallengeLeaderboard(challenge.id, {
        page: leaderboardIndex + 1,
        pageSize: 1,
        subgroup
      }) : {
        leaderboard: []
      }]))).flatMap(({
        leaderboard
      }) => leaderboard);
      const index = closeLeaderboard.findIndex(({
        id
      }) => id === currentUser.id);
      setCloseLeaderboard(closeLeaderboard.slice(Math.max(0, index - 1), index + 2).filter(({
        leaderboardIndex
      }) => typeof leaderboardIndex === 'number' && leaderboardIndex > defaultEnvironment.freeOfferEmployeesLeaderboardsMaxRowsCount));
    } catch (err) {
      if (!(err instanceof Error) || err?.name !== 'CancelledPromiseError') {
        console.error(err);
      }
    }
  }
  const selectedCount = Object.values(selectedUsers).filter(Boolean).length;
  const allSelected = selectedCount === leaderboard?.length;
  const Button = _Button || CustomButton;
  const isCompany = geogroup && geogroup.type === 'company' && Boolean(currentPartner) || false;
  const displaySubgroups = !fullLeaderboardsDisabled && !challenge?.isLocal;
  const hasSiteAssignment = (leaderboard || []).some(item => item.subGroups?.some(({
    type
  }) => type === 'companySite'));
  const hasTeamAssignment = (leaderboard || []).some(item => item.subGroups?.some(({
    type
  }) => type === 'companyTeam'));
  const showSiteColumn = Boolean(sites && sites.length > 1 && hasSiteAssignment);
  const showTeamColumn = Boolean(teams && teams.length > 1 && hasTeamAssignment);
  const isTeamPodium = initialDisplayType.current !== 'individual';
  const teamLeaderboard = challenge && userChallenge && userChallenge.subgroupsLeaderboards?.find(({
    subGroupType
  }) => subGroupType === displayType) || null;
  return /*#__PURE__*/React.createElement(React.Fragment, null, !disableTitle && !exporting && /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    height: 42,
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(_Typography, {
    fontSize: "1.25rem",
    fontWeight: 600
  }, t('commons.challenges.awards'))), challenge && enablePodium && podium && /*#__PURE__*/React.createElement(_Box, {
    alignItems: "flex-end",
    display: {
      xs: 'none',
      lg: 'flex'
    },
    gap: 2,
    marginBottom: 3,
    marginTop: 12
  }, podium.map((item, index) => {
    const {
      bgColor,
      heightRatio,
      individualMedal,
      teamMedal
    } = podiumRankMap[item?.rank || index + 1];
    return /*#__PURE__*/React.createElement(_Box, {
      alignItems: "center",
      bgcolor: bgColor,
      borderRadius: 4,
      display: "flex",
      flexDirection: "column",
      justifyContent: "flex-end",
      key: index,
      width: "calc((100% - 32px) / 3)"
    }, /*#__PURE__*/React.createElement(_Box, {
      alignItems: "center",
      display: "flex",
      flexDirection: "column",
      marginTop: -9,
      sx: challenge.period === 'past' && item?.rank === 1 ? {
        backgroundImage: `url(${stars})`,
        backgroundPosition: 'top center',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '200px auto'
      } : {},
      width: "100%"
    }, /*#__PURE__*/React.createElement(_Box, {
      alignItems: "center",
      bgcolor: bgColor,
      borderRadius: 15,
      display: "flex",
      height: 102,
      justifyContent: "center",
      width: 102
    }, /*#__PURE__*/React.createElement(_Box, {
      bgcolor: "#fff",
      border: "1px solid #DDE0E7",
      borderRadius: 12,
      height: 96,
      width: 96
    }, isTeamPodium || !item ? /*#__PURE__*/React.createElement("img", {
      height: "100%",
      src: teamMedal,
      width: "100%"
    }) : /*#__PURE__*/React.createElement(UserAvatar, {
      bgColor: "#fff",
      color: palette.primary.main,
      size: 94,
      user: item
    })))), /*#__PURE__*/React.createElement(_Box, {
      alignItems: "center",
      display: "flex",
      flexDirection: "column",
      gap: 2,
      height: 220 * heightRatio,
      justifyContent: item ? 'flex-start' : 'center',
      paddingBottom: 3,
      paddingTop: 3,
      paddingX: 3,
      width: "100%"
    }, item ? /*#__PURE__*/React.createElement(React.Fragment, null, !isTeamPodium && item && /*#__PURE__*/React.createElement(_Box, {
      marginTop: -5.5
    }, /*#__PURE__*/React.createElement("img", {
      src: individualMedal,
      width: 88
    })), /*#__PURE__*/React.createElement(_Typography, {
      noWrap: true,
      align: "center",
      fontSize: "1.15rem",
      fontWeight: 700
    }, item.username || t('commons.news.deleted_user')), /*#__PURE__*/React.createElement(_Typography, {
      noWrap: true,
      align: "center",
      fontSize: "1.1rem",
      fontWeight: 700
    }, getProgressValue(challenge.type, item.progressValue)), /*#__PURE__*/React.createElement(_Box, {
      alignItems: "center",
      display: "flex",
      flexDirection: "column",
      gap: 1
    }, isTeamPodium && /*#__PURE__*/React.createElement(_Typography, {
      noWrap: true,
      align: "center",
      variant: "body2"
    }, t('commons.challenges.participants', {
      count: item.participants || 0
    })), isTeamPodium && item.participants && item.progressValue ? /*#__PURE__*/React.createElement(_Typography, {
      noWrap: true,
      align: "center",
      variant: "body2"
    }, t('commons.stats.on_average', {
      value: getProgressValue(challenge.type, item.progressValue / item.participants)
    })) : /*#__PURE__*/React.createElement(React.Fragment, null))) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(_Typography, {
      align: "center",
      fontSize: "1.15rem",
      fontWeight: 700
    }, "/"))));
  })), !exporting && challenge && /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    gap: 3
  }, isCompany && enableCompetitionTypes ? /*#__PURE__*/React.createElement(_Tabs, {
    onChange: (_, type) => setDisplayType?.(type),
    scrollButtons: false,
    sx: {
      flexGrow: 1
    },
    value: displayType,
    variant: "scrollable"
  }, companyTabs.filter(({
    key
  }) => {
    if (key === 'companySite') return sites && sites.length >= 2;
    if (key === 'companyTeam') return teams && teams.length >= 2;
    if (key === 'companySubsidiary') return subsidiaries && subsidiaries.length >= 2;
    if (key === 'companyRegion') return regions && regions.length >= 2;
    return true;
  }).map(({
    key,
    labelKey
  }) => {
    const isDefault = challenge.collaborationType === 'individual' || !challenge.subGroupDisplay ? key === 'individual' : challenge.subGroupDisplay === key;
    return {
      key,
      isDefault,
      labelKey
    };
  }).sort(a => a.isDefault ? -1 : 1).map(({
    key,
    labelKey,
    isDefault
  }) => {
    return /*#__PURE__*/React.createElement(_Tab, {
      key: key,
      label: t(isDefault ? 'commons.challenge_details.competition_types.default' : labelKey),
      sx: {
        textTransform: 'initial'
      },
      value: key
    });
  })) : /*#__PURE__*/React.createElement(_Box, {
    flexGrow: 1
  }), handleExport && challenge.period !== 'future' && (displayType === 'individual' || !fullLeaderboardsDisabled || defaultEnvironment.freeOfferSubgroupsLeaderboardsMaxRowsCount > 0) && /*#__PURE__*/React.createElement(_Box, {
    flexShrink: 0
  }, /*#__PURE__*/React.createElement(Button, {
    disabled: downloading || !challenge || !leaderboard || count === undefined,
    onClick: ({
      currentTarget
    }) => handleExport(currentTarget),
    size: "small",
    startIcon: downloading ? /*#__PURE__*/React.createElement(_CircularProgress, {
      color: "inherit",
      size: 16,
      thickness: 4
    }) : /*#__PURE__*/React.createElement(ExportIcon, null),
    variant: "outlined"
  }, t('commons.actions.export')))), !exporting && challenge && challenge.period !== 'future' && (displayType === 'individual' ? userChallenge?.rank && /*#__PURE__*/React.createElement(_Box, {
    border: "1px solid #9CBDF4",
    borderRadius: 2,
    padding: 2,
    width: "100%"
  }, /*#__PURE__*/React.createElement(_Typography, {
    variant: "body2"
  }, leaderboard ? /*#__PURE__*/React.createElement(Trans, {
    components: [/*#__PURE__*/React.createElement("span", {
      key: 0
    })],
    i18nKey: "commons.challenge_details.rank",
    values: {
      nbMembers: count,
      rank: toOrdinal(userChallenge.rank, {
        inclusive: true
      })
    }
  }) : /*#__PURE__*/React.createElement(_Skeleton, {
    variant: "text",
    width: 200
  }))) : !fullLeaderboardsDisabled && /*#__PURE__*/React.createElement(_Box, {
    border: "1px solid #9CBDF4",
    borderRadius: 2,
    padding: 2,
    width: "100%"
  }, /*#__PURE__*/React.createElement(_Typography, {
    variant: "body2"
  }, leaderboard ? /*#__PURE__*/React.createElement(Trans, {
    components: [/*#__PURE__*/React.createElement("span", {
      key: 0
    })],
    i18nKey: "commons.challenges.rank_team",
    values: {
      rank: teamLeaderboard ? toOrdinal(teamLeaderboard.rank, {
        feminine: true
      }) : '-',
      count: teamLeaderboard?.countSubgroupOfSameType || '-'
    }
  }) : /*#__PURE__*/React.createElement(_Skeleton, {
    variant: "text",
    width: 200
  })))), !exporting && !disableContact && selectedCount > 0 && displayType === 'individual' && userType === 'admin' && /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    bgcolor: "#F3F6FF",
    borderRadius: 4,
    display: "flex",
    gap: 3,
    justifyContent: "space-between",
    paddingX: 3,
    paddingY: 2
  }, /*#__PURE__*/React.createElement(_Typography, {
    color: "primary"
  }, t('commons.challenge_details.table.selected_members', {
    count: selectedCount
  })), /*#__PURE__*/React.createElement(Button, {
    disabled: !leaderboard || selectedCount === 0,
    onClick: () => openContactDialog(true),
    startIcon: /*#__PURE__*/React.createElement(ContactIcon, null),
    variant: "contained"
  }, t('commons.actions.contact'))), /*#__PURE__*/React.createElement(_TableContainer, {
    component: _Paper,
    elevation: 0
  }, /*#__PURE__*/React.createElement(_Table, null, /*#__PURE__*/React.createElement(_TableHead, null, /*#__PURE__*/React.createElement(_TableRow, null, !exporting && !disableContact && displayType === 'individual' && userType === 'admin' && /*#__PURE__*/React.createElement(_TableCell, {
    align: "center",
    padding: "checkbox"
  }, /*#__PURE__*/React.createElement(_Checkbox, {
    checked: allSelected,
    color: "primary",
    disabled: !leaderboard,
    indeterminate: !allSelected && selectedCount > 0,
    onChange: () => {
      if (selectedCount > 0) selectUsers({});else selectUsers(leaderboard?.reduce((res, {
        id
      }) => {
        if (id) res[id] = true;
        return res;
      }, {}) || {});
    },
    size: "small"
  })), /*#__PURE__*/React.createElement(_TableCell, {
    align: "center",
    padding: "checkbox",
    sx: {
      width: 42
    }
  }, "#"), /*#__PURE__*/React.createElement(_TableCell, null, displayType === 'individual' ? t('commons.challenge_details.table.pseudonym') : displayType === 'companySite' ? t('commons.challenge_details.table.site') : displayType === 'companySubsidiary' ? t('commons.challenge_details.table.subsidiary') : displayType === 'companyRegion' ? t('commons.challenge_details.table.region') : t('commons.challenge_details.table.team')), displayType !== 'individual' && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(_TableCell, {
    sx: {
      textAlign: 'end',
      width: 150
    }
  }, t('commons.challenge_details.table.participants')), /*#__PURE__*/React.createElement(_TableCell, {
    sx: {
      textAlign: 'end',
      width: 150
    }
  }, t('commons.challenge_details.table.average_per_participant'))), isCompany && displayType === 'individual' && /*#__PURE__*/React.createElement(React.Fragment, null, displaySubgroups && (!subgroup || subgroup.subGroupType === 'companyTeam') && showSiteColumn && /*#__PURE__*/React.createElement(_TableCell, null, t('commons.challenge_details.table.site')), displaySubgroups && (!subgroup || subgroup.subGroupType === 'companySite') && showTeamColumn && /*#__PURE__*/React.createElement(_TableCell, null, t('commons.challenge_details.table.team')), !exporting && displaySubgroups && (!subgroup || subgroup.subGroupType === 'companySubsidiary') && subsidiaries !== null && /*#__PURE__*/React.createElement(_TableCell, null, t('commons.challenge_details.table.subsidiary')), !exporting && displaySubgroups && (!subgroup || subgroup.subGroupType === 'companyRegion') && regions !== null && /*#__PURE__*/React.createElement(_TableCell, null, t('commons.challenge_details.table.region'))), (!challenge || challenge.type !== 'noTarget') && /*#__PURE__*/React.createElement(_TableCell, {
    style: {
      textAlign: 'end',
      width: 150
    }
  }, challenge ? rankFieldLabel ?? t('commons.challenge_details.table.performance', {
    context: challenge.type === 'bttf' ? 'bttf' : challenge.type === 'custom' ? 'custom' : challenge.type === 'traveledDistance' ? 'distance' : challenge.type === 'cyclingDays' ? 'days' : challenge.type === 'homeWorkJourneysCount' || challenge.type === 'journeysCount' ? 'journeys' : ''
  }) : /*#__PURE__*/React.createElement(_Skeleton, {
    variant: "text",
    width: 50
  })))), /*#__PURE__*/React.createElement(_TableBody, null, challenge && leaderboard ? /*#__PURE__*/React.createElement(React.Fragment, null, leaderboard.map((item, index) => /*#__PURE__*/React.createElement(Item, {
    challenge: challenge,
    disableContact: disableContact,
    displaySubgroups: displaySubgroups,
    displayType: displayType,
    exporting: exporting,
    index: index,
    isCompany: isCompany,
    item: item,
    key: item.id || index,
    leaderboard: leaderboard,
    onRowClick: item.id ? onRowClick : undefined,
    own: currentUser?.id === item.id,
    rankField: rankField,
    regions: regions,
    selectedUsers: selectedUsers,
    selectUsers: selectUsers,
    showSiteColumn: showSiteColumn,
    showTeamColumn: showTeamColumn,
    subgroup: subgroup,
    subsidiaries: subsidiaries,
    teams: teams,
    userType: userType
  })), fullLeaderboardsDisabled && closeLeaderboard.map((item, index) => /*#__PURE__*/React.createElement(Item, {
    challenge: challenge,
    dashed: index === 0,
    disableContact: disableContact,
    disableRank: index === 0 && closeLeaderboard[0].rank === leaderboard[leaderboard.length - 1].rank,
    displaySubgroups: displaySubgroups,
    displayType: displayType,
    exporting: exporting,
    index: index,
    isCompany: isCompany,
    item: item,
    key: item.id || index,
    leaderboard: closeLeaderboard,
    onRowClick: item.id ? onRowClick : undefined,
    own: currentUser?.id === item.id,
    regions: regions,
    selectedUsers: selectedUsers,
    selectUsers: selectUsers,
    showSiteColumn: showSiteColumn,
    showTeamColumn: showTeamColumn,
    subgroup: subgroup,
    subsidiaries: subsidiaries,
    teams: teams,
    userType: userType
  }))) : new Array(10).fill(null).map((_, index) => /*#__PURE__*/React.createElement(Item, {
    challenge: challenge,
    disableContact: disableContact,
    displaySubgroups: displaySubgroups,
    displayType: displayType,
    exporting: exporting,
    index: index,
    isCompany: isCompany,
    key: index,
    leaderboard: leaderboard,
    regions: regions,
    selectedUsers: selectedUsers,
    showSiteColumn: showSiteColumn,
    showTeamColumn: showTeamColumn,
    subgroup: subgroup,
    subsidiaries: subsidiaries,
    teams: teams,
    userType: userType
  }))), fullLeaderboardsDisabled && count !== undefined && (displayType === 'individual' ? count > defaultEnvironment.freeOfferEmployeesLeaderboardsMaxRowsCount : count > defaultEnvironment.freeOfferSubgroupsLeaderboardsMaxRowsCount) && /*#__PURE__*/React.createElement(FakeItems, {
    Card: FakeItemsCard,
    challenge: challenge,
    disableContact: disableContact,
    displaySubgroups: displaySubgroups,
    displayType: displayType,
    exporting: exporting,
    isCompany: isCompany,
    isEventWithOffer: isEventWithOffer,
    leaderboard: leaderboard,
    regions: regions,
    selectedUsers: selectedUsers,
    showSiteColumn: showSiteColumn,
    showTeamColumn: showTeamColumn,
    subgroup: subgroup,
    subsidiaries: subsidiaries,
    teams: teams,
    userType: userType
  }))), !fullLeaderboardsDisabled && !exporting && displayType === 'individual' && (count === undefined || !leaderboard || leaderboard.length < count) && /*#__PURE__*/React.createElement(Button, {
    disabled: count === undefined || !leaderboard || loading,
    onClick: () => setPage(page + 1),
    size: "medium",
    startIcon: loading && /*#__PURE__*/React.createElement(_CircularProgress, {
      color: "inherit",
      size: 16,
      thickness: 4
    }),
    sx: {
      alignSelf: 'center'
    },
    variant: "outlined"
  }, t('commons.challenge_details.table.see_more')), /*#__PURE__*/React.createElement(ContactDialog, {
    Button: Button,
    challenge: challenge,
    currentUser: currentUser,
    Dialog: Dialog,
    onClose: () => {
      openContactDialog(false);
      selectUsers({});
    },
    open: contactDialogOpen,
    selectedUsersIds: leaderboard?.reduce((res, {
      id
    }) => {
      if (id && selectedUsers[id]) res.push(id);
      return res;
    }, []) || []
  }));
}
export { fakeEmployees, individualChallengeLeaderboardPageSize };